import { Component, DoCheck, Input, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { CeoEmailMessageI, CeoEmailPreviewI, ceoEmailError } from 'src/app/interfaces/ceo-emails.interface';

/**
 * ⭐ THE EMAIL EXACTLY AS ONE RECIPIENT WILL GET IT.
 *
 * ⚠️ RENDERED BY THE BACKEND, NOT HERE. The preview, the test send and the real send
 * all go through account-backend's `renderEmail`, so the name rules, the sentence
 * repair around `{{name}}` and the fixed Totalum layout cannot drift between what the
 * CEO reviews and what customers receive.
 *
 * ⚠️ `ngDoCheck`, NOT `ngOnChanges`. The editors bind `[message]` to an object they
 * mutate through `ngModel`, so the reference never changes; a cheap JSON snapshot is
 * what notices a keystroke. Requests are debounced and a stale answer is discarded.
 *
 * The HTML goes into a SANDBOXED iframe (no scripts, links open in a new tab), so an
 * email body can never run anything inside the admin panel.
 */
@Component({
  selector: 'app-ceo-email-preview',
  templateUrl: './ceo-email-preview.component.html',
})
export class CeoEmailPreviewComponent implements DoCheck, OnDestroy {
  @Input() message: CeoEmailMessageI | null = null;
  @Input() kind: 'campaign' | 'direct' = 'campaign';
  /** Whose name and language to render with. Null = a sample recipient. */
  @Input() userId: string | null = null;
  /** Hides the desktop/mobile toggle and caps the height, for the contact modal. */
  @Input() compact = false;

  preview: CeoEmailPreviewI | null = null;
  html: SafeHtml | null = null;
  loading = false;
  error = '';
  device: 'desktop' | 'mobile' = 'desktop';

  private snapshot = '';
  private timer: any = null;
  private requestSeq = 0;

  constructor(private api: ApiSdkService, private sanitizer: DomSanitizer) {}

  ngDoCheck(): void {
    const next = JSON.stringify([this.message, this.kind, this.userId]);
    if (next === this.snapshot) return;
    this.snapshot = next;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.load(), this.preview ? 450 : 0);
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
  }

  private async load(): Promise<void> {
    if (!this.message) return;
    const seq = ++this.requestSeq;
    this.loading = true;
    try {
      const result = await this.api.previewCeoEmail({ kind: this.kind, message: this.message, userId: this.userId });
      if (seq !== this.requestSeq) return;
      const data: CeoEmailPreviewI = result?.success?.data;
      if (data) {
        this.preview = data;
        this.error = '';
        // Links open outside the preview; the sandbox below blocks scripts and top navigation.
        this.html = data.html
          ? this.sanitizer.bypassSecurityTrustHtml(data.html.replace('<head>', '<head><base target="_blank" />'))
          : null;
      } else {
        this.error = ceoEmailError(result, 'Could not render the preview');
      }
    } catch {
      if (seq === this.requestSeq) this.error = 'Could not render the preview';
    } finally {
      if (seq === this.requestSeq) this.loading = false;
    }
  }

  get nameNote(): string {
    if (!this.preview) return '';
    const hasVariable = /\{\{\s*name\s*\}\}/i.test(`${this.message?.subject} ${this.message?.heading || ''} ${this.message?.body}`);
    if (!hasVariable) return '';
    return this.preview.firstName
      ? `{{name}} → "${this.preview.firstName}"`
      : `{{name}} left out — "${this.preview.recipientName || 'no name'}" does not look like a first name`;
  }
}
